/**
 * Fetcher & mutator pure functions untuk finance domain.
 *
 * Subdomain:
 *   - utang-admin
 *   - utang-mitra
 *   - invoice-agen (list, latest, list-invoice)
 *   - piutang-agen (list, count, count-all, pay, export, import)
 *   - piutang-member (list, count, count-all, detail-list, pay, export, import)
 *   - hpp (list, detail-list, pay, export, import)
 *   - biaya-operasional (list, get-total, get-total-branch, get/:id, mutations)
 *   - tagihan-agen
 */
import http from "@/config/http";

/* ============================================================
 * Utang Admin
 * ============================================================ */

export async function fetchUtangAdmin(params = {}) {
  const { data } = await http.get("/api/v1/finance/utang-admin", { params });
  return data;
}

export async function fetchUtangAdminById(id) {
  const { data } = await http.get(`/api/v1/finance/utang-admin/${id}`);
  return data;
}

export async function updateUtangAdmin({ id, payload }) {
  const { data } = await http.put(
    `/api/v1/finance/utang-admin/update/${id}`,
    payload
  );
  return data;
}

/* ============================================================
 * Utang Mitra
 * ============================================================ */

export async function fetchUtangMitra(params = {}) {
  const { data } = await http.get("/api/v1/finance/utang-mitra", { params });
  return data;
}

export async function fetchUtangMitraById(id) {
  const { data } = await http.get(`/api/v1/finance/utang-mitra/get/${id}`);
  return data;
}

export async function fetchUtangMitraSummary(params = {}) {
  const { data } = await http.get(
    "/api/v1/finance/utang-mitra/getSumMitra",
    { params }
  );
  return data;
}

export async function createUtangMitra(payload) {
  const { data } = await http.post(
    "/api/v1/finance/utang-mitra/create",
    payload
  );
  return data;
}

export async function updateUtangMitra({ id, payload }) {
  const { data } = await http.put(
    `/api/v1/finance/utang-mitra/update/${id}`,
    payload
  );
  return data;
}

export async function exportUtangMitra(params = {}, config = {}) {
  const { data } = await http.get("/api/v1/finance/utang-mitra/export", {
    params,
    responseType: "blob",
    ...config,
  });
  return data;
}

export async function importUtangMitra(payload, config = {}) {
  const { data } = await http.post(
    "/api/v1/finance/utang-mitra/import",
    payload,
    config
  );
  return data;
}

/* ============================================================
 * Invoice Agen
 * ============================================================ */

export async function fetchInvoiceAgen(params = {}) {
  const { data } = await http.get("/api/v1/finance/invoice-agen", { params });
  return data;
}

export async function fetchInvoiceAgenLatest() {
  const { data } = await http.get(
    "/api/v1/finance/invoice-agen/latest-invoice"
  );
  return data;
}

export async function fetchInvoiceMitra(params = {}) {
  const { data } = await http.get(
    "/api/v1/finance/invoice-agen/list-invoice",
    { params }
  );
  return data;
}

/* ============================================================
 * Piutang Agen
 * ============================================================ */

export async function fetchPiutangAgen(params = {}) {
  const { data } = await http.get("/api/v1/finance/piutang-agen", { params });
  return data;
}

export async function fetchPiutangAgenCount(params = {}) {
  const { data } = await http.get("/api/v1/finance/piutang-agen/count", {
    params,
  });
  return data;
}

export async function fetchPiutangAgenCountAll(params = {}) {
  const { data } = await http.get("/api/v1/finance/piutang-agen/count-all", {
    params,
  });
  return data;
}

export async function payPiutangAgen(payload) {
  const { data } = await http.post(
    "/api/v1/finance/piutang-agen/pay",
    payload
  );
  return data;
}

export async function exportPiutangAgen(params = {}, config = {}) {
  const { data } = await http.get("/api/v1/finance/piutang-agen/export", {
    params,
    responseType: "blob",
    ...config,
  });
  return data;
}

export async function importPiutangAgen(payload, config = {}) {
  const { data } = await http.post(
    "/api/v1/finance/piutang-agen/import",
    payload,
    config
  );
  return data;
}

/* ============================================================
 * Piutang Member
 * ============================================================ */

export async function fetchPiutangMember(params = {}) {
  const { data } = await http.get("/api/v1/finance/piutang-member", { params });
  return data;
}

export async function fetchPiutangMemberCount(params = {}) {
  const { data } = await http.get("/api/v1/finance/piutang-member/count", {
    params,
  });
  return data;
}

export async function fetchPiutangMemberCountAll(params = {}) {
  const { data } = await http.get(
    "/api/v1/finance/piutang-member/count-all",
    { params }
  );
  return data;
}

export async function fetchPiutangMemberDetailList(params = {}) {
  const { data } = await http.get(
    "/api/v1/finance/piutang-member/detail-list",
    { params }
  );
  return data;
}

export async function payPiutangMember(payload) {
  const { data } = await http.post(
    "/api/v1/finance/piutang-member/pay",
    payload
  );
  return data;
}

export async function exportPiutangMember(params = {}, config = {}) {
  const { data } = await http.get("/api/v1/finance/piutang-member/export", {
    params,
    responseType: "blob",
    ...config,
  });
  return data;
}

export async function importPiutangMember(payload, config = {}) {
  const { data } = await http.post(
    "/api/v1/finance/piutang-member/import",
    payload,
    config
  );
  return data;
}

/* ============================================================
 * Bayar HPP
 * ============================================================ */

export async function fetchBayarHpp(params = {}) {
  const { data } = await http.get("/api/v1/finance/hpp", { params });
  return data;
}

export async function fetchBayarHppDetailList(params = {}) {
  const { data } = await http.get("/api/v1/finance/hpp/detail-list", {
    params,
  });
  return data;
}

export async function payHpp(payload) {
  const { data } = await http.post("/api/v1/finance/hpp/pay", payload);
  return data;
}

export async function exportHpp(params = {}, config = {}) {
  const { data } = await http.get("/api/v1/finance/hpp/export", {
    params,
    responseType: "blob",
    ...config,
  });
  return data;
}

export async function importHpp(payload, config = {}) {
  const { data } = await http.post(
    "/api/v1/finance/hpp/import",
    payload,
    config
  );
  return data;
}

/* ============================================================
 * Biaya Operasional
 * ============================================================ */

export async function fetchBiayaOperasional(params = {}) {
  const { data } = await http.get("/api/v1/finance/biaya-operasional", {
    params,
  });
  return data;
}

export async function fetchBiayaOperasionalById(id) {
  const { data } = await http.get(`/api/v1/finance/biaya-operasional/get/${id}`);
  return data;
}

export async function fetchBiayaOperasionalTotal(params = {}) {
  const { data } = await http.get(
    "/api/v1/finance/biaya-operasional/get-total",
    { params }
  );
  return data;
}

export async function fetchBiayaOperasionalTotalBranch(params = {}) {
  const { data } = await http.get(
    "/api/v1/finance/biaya-operasional/get-total-branch",
    { params }
  );
  return data;
}

export async function createBiayaOperasional(payload) {
  const { data } = await http.post(
    "/api/v1/finance/biaya-operasional/create",
    payload
  );
  return data;
}

export async function updateBiayaOperasional({ id, payload }) {
  const { data } = await http.put(
    `/api/v1/finance/biaya-operasional/update/${id}`,
    payload
  );
  return data;
}

export async function deleteBiayaOperasional(id) {
  const { data } = await http.delete(
    `/api/v1/finance/biaya-operasional/delete/${id}`
  );
  return data;
}

export async function importBiayaOperasional(payload, config = {}) {
  const { data } = await http.post(
    "/api/v1/finance/biaya-operasional/import",
    payload,
    config
  );
  return data;
}

/* ============================================================
 * Tagihan Agen
 * ============================================================ */

export async function fetchTagihanAgen(params = {}) {
  const { data } = await http.get("/api/v1/finance/tagihan-agen", { params });
  return data;
}

export async function fetchTagihanAgenByAgen(agenId, params = {}) {
  const { data } = await http.get(`/api/v1/finance/tagihan-agen/${agenId}`, {
    params,
  });
  return data;
}
